import type { ItemId } from '@/data/items';

/**
 * Crafting recipes.
 *
 * Everything here is made from things found while doing something else —
 * fiber from hoeing, driftwood snagged on a cast, shells from the wet sand,
 * feathers left by the chickens. Nothing is bought, so the way to a better rod
 * is to keep playing the game rather than to grind money.
 *
 * Deliberately small and declarative: Phase 6 grows this into weapon trees and
 * will want the same shape.
 */

export interface Ingredient {
  readonly item: ItemId;
  readonly count: number;
}

export interface Recipe {
  readonly id: string;
  /** Item produced, or null for a recipe that only grants an effect. */
  readonly output: ItemId | null;
  readonly outputCount: number;
  readonly inputs: readonly Ingredient[];
  /** Grouping for the crafting list. */
  readonly category: 'rod' | 'lure' | 'bag' | 'blade';
  readonly note: string;
  /** Extra bag slots, for upgrades that produce no item. */
  readonly bagSlots?: number;
  /** Display name when there is no output item to name it after. */
  readonly name?: string;
  /** Bag capacity this recipe requires, so upgrades come in order. */
  readonly requiresCapacity?: number;
  /**
   * True for patterns nobody knows until they are taught.
   *
   * A locked recipe is not listed at all rather than shown greyed out: the
   * point of it is being handed a pattern, and a slot in the craft list
   * saying "not yet" gives that away weeks early.
   */
  readonly locked?: boolean;
}

const CRAFTABLES: readonly Recipe[] = [
  // --- Blades -------------------------------------------------------------
  // Both cost pearls, which only come out of the deep. A weapon that changes
  // how combat plays should be paid for with the part of the game it changes.
  {
    id: 'rootcleaver',
    output: 'rootcleaver',
    outputCount: 1,
    category: 'blade',
    note: 'Slow and enormous. Commit, or miss.',
    inputs: [
      { item: 'mat:driftwood', count: 12 },
      { item: 'mat:pearl', count: 2 },
      { item: 'mat:blightSap', count: 4 },
    ],
  },
  {
    id: 'tidewhisper',
    output: 'tidewhisper',
    outputCount: 1,
    category: 'blade',
    note: 'Long and quick. Small cuts, from further out.',
    inputs: [
      { item: 'mat:shell', count: 10 },
      { item: 'mat:pearl', count: 2 },
      { item: 'mat:mothDust', count: 6 },
    ],
  },

  {
    id: 'emberbrand',
    output: 'emberbrand',
    outputCount: 1,
    category: 'blade',
    locked: true,
    note: 'Mira’s pattern. Balanced, which the other two are not.',
    inputs: [
      { item: 'mat:driftwood', count: 8 },
      { item: 'mat:blightSap', count: 4 },
      { item: 'mat:mothDust', count: 3 },
      // Pearls, like both other blades. Knowing the pattern is what the quest
      // buys; it does not buy an exemption from what a blade costs.
      { item: 'mat:pearl', count: 2 },
    ],
  },

  // --- Rods ---------------------------------------------------------------
  {
    id: 'fiberglassRod',
    output: 'fiberglassRod',
    outputCount: 1,
    category: 'rod',
    note: 'Reaches trout and bass.',
    inputs: [
      { item: 'mat:driftwood', count: 5 },
      { item: 'mat:fiber', count: 8 },
    ],
  },
  {
    id: 'steelRod',
    output: 'steelRod',
    outputCount: 1,
    category: 'rod',
    note: 'Holds pike, catfish and sea bass.',
    inputs: [
      { item: 'mat:driftwood', count: 4 },
      { item: 'mat:fiber', count: 14 },
      { item: 'mat:shell', count: 6 },
    ],
  },
  {
    id: 'masterRod',
    output: 'masterRod',
    outputCount: 1,
    category: 'rod',
    note: 'Anything that swims.',
    inputs: [
      { item: 'mat:pearl', count: 2 },
      { item: 'mat:fiber', count: 20 },
      { item: 'mat:shell', count: 10 },
      { item: 'mat:driftwood', count: 6 },
    ],
  },

  // --- Lures --------------------------------------------------------------
  {
    id: 'spinner',
    output: 'bait:spinner',
    outputCount: 1,
    category: 'lure',
    note: 'Bass, pike and mackerel.',
    inputs: [
      { item: 'mat:shell', count: 3 },
      { item: 'mat:fiber', count: 2 },
    ],
  },
  {
    id: 'flyLure',
    output: 'bait:flyLure',
    outputCount: 1,
    category: 'lure',
    note: 'Trout and grayling only.',
    inputs: [
      { item: 'mat:feather', count: 3 },
      { item: 'mat:fiber', count: 4 },
    ],
  },
  {
    id: 'spoonLure',
    output: 'bait:spoonLure',
    outputCount: 1,
    category: 'lure',
    note: 'The big saltwater fish.',
    inputs: [
      { item: 'mat:shell', count: 6 },
      { item: 'mat:driftwood', count: 2 },
      { item: 'mat:fiber', count: 4 },
    ],
  },
  {
    id: 'glowLure',
    output: 'bait:glowLure',
    outputCount: 1,
    category: 'lure',
    note: 'The only thing the night fish notice.',
    inputs: [
      { item: 'mat:pearl', count: 1 },
      { item: 'mat:fiber', count: 6 },
    ],
  },
];

/**
 * Bag upgrades.
 *
 * Each needs the previous size, so they are a ladder rather than a menu, and
 * each costs materials from a different activity — you cannot reach the last
 * one by only farming or only fishing.
 */
const BAG_UPGRADES: readonly Recipe[] = [
  {
    id: 'bag18',
    output: null,
    outputCount: 0,
    category: 'bag',
    name: 'Roomier Backpack',
    note: 'Six more slots.',
    bagSlots: 6,
    requiresCapacity: 12,
    inputs: [
      { item: 'mat:fiber', count: 12 },
      { item: 'mat:driftwood', count: 4 },
    ],
  },
  {
    id: 'bag24',
    output: null,
    outputCount: 0,
    category: 'bag',
    name: 'Traveller\u2019s Backpack',
    note: 'Six more again.',
    bagSlots: 6,
    requiresCapacity: 18,
    inputs: [
      { item: 'mat:fiber', count: 20 },
      { item: 'mat:shell', count: 8 },
      { item: 'mat:feather', count: 4 },
    ],
  },
  {
    id: 'bag30',
    output: null,
    outputCount: 0,
    category: 'bag',
    name: 'Expedition Pack',
    note: 'As much as you can carry.',
    bagSlots: 6,
    requiresCapacity: 24,
    inputs: [
      { item: 'mat:fiber', count: 30 },
      { item: 'mat:pearl', count: 1 },
      { item: 'mat:driftwood', count: 10 },
    ],
  },
];

export const RECIPES: readonly Recipe[] = [...CRAFTABLES, ...BAG_UPGRADES];

export function recipeName(recipe: Recipe): string {
  return recipe.name ?? recipe.id;
}

export function recipeFor(id: string): Recipe | undefined {
  return RECIPES.find((recipe) => recipe.id === id);
}
